import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const assignmentKeys = {
  all: ['projectTaskAssignments'],
  lists: () => [...assignmentKeys.all, 'list'],
  byProject: (projectId) => [...assignmentKeys.all, 'project', projectId],
  byTemplate: (templateId) => [...assignmentKeys.all, 'template', templateId],
  specific: (projectId, templateId) => [...assignmentKeys.all, 'specific', projectId, templateId],
};

/**
 * Hook para obtener asignaciones por proyecto
 * @param {string} projectId - UUID del proyecto
 * @param {Object} options - Opciones adicionales para la query
 */
export const useAssignmentsByProject = (projectId, options = {}) => {
  return useQuery({
    queryKey: assignmentKeys.byProject(projectId),
    queryFn: async () => {
      const response = await apiClient.get(`project_task_assignments/by-project/${projectId}`);
      return response.data;
    },
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para obtener asignaciones por template
 * @param {string} templateId - UUID del template
 * @param {Object} options - Opciones adicionales para la query
 */
export const useAssignmentsByTemplate = (templateId, options = {}) => {
  return useQuery({
    queryKey: assignmentKeys.byTemplate(templateId),
    queryFn: async () => {
      const response = await apiClient.get(`project_task_assignments/by-template/${templateId}`);
      return response.data;
    },
    enabled: !!templateId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Hook para crear asignación de template a proyecto
 */
export const useCreateProjectTaskAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (assignmentData) => {
      const response = await apiClient.post('project_task_assignments', assignmentData);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byProject(variables.uuid_project) });
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byTemplate(variables.uuid_task_template) });
    },
    onError: (error) => {
      console.error('Error creating task assignment:', error);
    },
  });
};

/**
 * Hook para eliminar asignación de template
 */
export const useRemoveProjectTaskAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, templateId }) => {
      const response = await apiClient.delete(`project_task_assignments/${projectId}/${templateId}`);
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byProject(variables.projectId) });
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byTemplate(variables.templateId) });
    },
    onError: (error) => {
      console.error('Error removing task assignment:', error);
    },
  });
};

/**
 * Hook para asignar múltiples templates
 */
export const useBulkAssignTemplates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ projectId, templateUuids, assignedBy }) => {
      const response = await apiClient.post(`project_task_assignments/bulk-assign/${projectId}`, {
        template_uuids: templateUuids,
        assigned_by: assignedBy
      });
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: assignmentKeys.byProject(variables.projectId) });
      // Invalidar queries de templates afectados
      variables.templateUuids.forEach(templateId => {
        queryClient.invalidateQueries({ queryKey: assignmentKeys.byTemplate(templateId) });
      });
    },
    onError: (error) => {
      console.error('Error bulk assigning templates:', error);
    },
  });
};